import { Component, OnInit, ViewChild, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { Enterprise } from '../models/enterprise';

import { environment } from '../../../environments/environment';
import { EnterpriseService } from './enterprise.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from 'src/app/loader/loader.service';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-enterprise',
  templateUrl: './enterprise.component.html',
  styleUrls: ['./enterprise.component.scss'],
})
export class EnterpriseComponent implements OnInit, OnDestroy {
  env;
  enterprise: Enterprise;
  timeoutIDSuccess;
  timeoutIDError;

  private disabledMethodsForCode = ['BO', 'BI', 'DI', 'PX', 'DM', 'CM'];

  private unsubscribe$ = new Subject();

  @ViewChild('alertSuccess', { read: ElementRef, static: false })
  alertSuccess: ElementRef;

  @ViewChild('alertError', { read: ElementRef, static: false })
  alertError: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private enterpriseService: EnterpriseService,
    private renderer: Renderer2,
    private loaderService: LoaderService,
    public permissionsService: PermissionsService
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (this.permissionsService.isPermitted('settings')) {
        this.getEnterprise(this.env.selected._id);
      } else {
        this.router.navigateByUrl(`/dash/${params.cnpj}`);
      }
    });
  }

  onSubmit() {
    //-- Filtra pelos metodos nao deletados
    this.enterprise.metodos_pag = this.enterprise.metodos_pag.filter(method => {
      return method.tipo !== '' && !method.deleted;
    });

    //-- Altera estrutura para enviar ao backend
    this.enterprise.metodos_pag = this.enterprise.metodos_pag.map(method => {
      return {
        tipo: method.tipo,
        codigo: method.codigo,
        utilizado_pagto: method.utilizado_pagto,
        status: method.status,
      };
    });

    this.updateEnterprise(this.enterprise._id, this.enterprise);
  }

  addSuccessAlert(msg: string) {
    clearTimeout(this.timeoutIDSuccess);

    this.renderer.setProperty(this.alertSuccess.nativeElement, 'innerHTML', msg);
    this.renderer.setProperty(this.alertSuccess.nativeElement, 'hidden', false);

    this.timeoutIDSuccess = setTimeout(_ => {
      this.renderer.setProperty(this.alertSuccess.nativeElement, 'hidden', true);
    }, 8000);
  }

  addErrorAlert(msg: string) {
    clearTimeout(this.timeoutIDError);

    this.renderer.setProperty(this.alertError.nativeElement, 'innerHTML', msg);
    this.renderer.setProperty(this.alertError.nativeElement, 'hidden', false);

    this.timeoutIDError = setTimeout(_ => {
      this.renderer.setProperty(this.alertError.nativeElement, 'hidden', true);
    }, 8000);
  }

  async getEnterprise(id: string) {
    const loader = await this.loaderService.notifica({
      title: 'Buscando dados da empresa...',
      date: moment().toISOString(),
      type: 'loading',
    });

    this.enterprise = await this.enterpriseService.getEnterpriseById(id).toPromise();

    loader.dismiss();
  }

  async updateEnterprise(id: string, enterprise: Enterprise) {
    const loader = await this.loaderService.notifica({
      title: 'Atualizando empresa...',
      date: moment().toISOString(),
      type: 'loading',
    });

    const updEnterprise = await this.enterpriseService.updateEnterprise(id, enterprise).toPromise();

    if ('error' in updEnterprise) {
      this.addErrorAlert(updEnterprise.error);
    } else {
      this.addSuccessAlert(updEnterprise.ok);
    }

    loader.dismiss();
  }

  addMetodoPag() {
    this.enterprise.metodos_pag.push({ tipo: '', codigo: '', utilizado_pagto: null, status: '', deleted: false });
  }

  addChavePix() {
    if (this.enterprise.chaves_pix && this.enterprise.chaves_pix.length) {
      this.enterprise.chaves_pix.push({ tipo: '', codigo: '', status: '' });
    } else {
      this.enterprise.chaves_pix = [{ tipo: '', codigo: '', status: '' }];
    }
  }

  deleteMetodoPag(index) {
    this.enterprise.metodos_pag[index].deleted = true;
  }

  deleteChavePix(index) {
    this.enterprise.chaves_pix.splice(index, 1);
  }

  goBack(): void {
    this.router.navigateByUrl(`/settings/${this.env.selected.cnpj}`);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isMethodDisabled(method: string) {
    return this.disabledMethodsForCode.includes(method);
  }

  validateCPF(cpf: string): boolean {
    // lógica simplificada para validar CPF
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length !== 11) return false;
    // verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cpf)) return false;
    // validação dos dígitos verificadores
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    let resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;
    return true;
  }

  validateCNPJ(cnpj: string): boolean {
    // lógica simplificada para validar CNPJ
    cnpj = cnpj.replace(/[^\d]+/g, '');
    if (cnpj.length !== 14) return false;
    // verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cnpj)) return false;
    // validação dos dígitos verificadores
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    return true;
  }

  validateCellPhone(phone: string): boolean {
    const re = /^\d{11}$/;
    return re.test(phone);
  }

  validateEmail(email: string): boolean {
    // lógica simplificada para validar email
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  validateKey(chave: any): boolean {
    if (chave.tipo === 'CPF') {
      return this.validateCPF(chave.codigo);
    } else if (chave.tipo === 'CNPJ') {
      return this.validateCNPJ(chave.codigo);
    } else if (chave.tipo === 'CELULAR') {
      return this.validateCellPhone(chave.codigo);
    } else if (chave.tipo === 'EMAIL') {
      return this.validateEmail(chave.codigo);
    }
    return false;
  }
}
